import { useEffect, useRef, useState } from 'react';
import { api, describeError, type VideoLibrary } from './api.js';

/**
 * Anthony's screen recordings for AI videos: upload a clip with a one-line description of what
 * it shows (Alfred picks clips by that). Presenter lines made by HeyGen appear here too.
 */
export function VideoFootageSettings() {
  const [lib, setLib] = useState<VideoLibrary | null>(null);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = () =>
    api
      .videoLibrary()
      .then(setLib)
      .catch(() => {});
  useEffect(() => void refresh(), []);

  const upload = async (file: File) => {
    setBusy(true);
    setMessage(`Uploading ${file.name}…`);
    try {
      await api.uploadFootage(file, description.trim());
      setDescription('');
      setMessage('Added to the library.');
      await refresh();
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const record = async () => {
    setBusy(true);
    try {
      await api.recordFootage(lib?.project.id);
      setMessage(
        'Recording now (up to about 15 minutes, it waits for a live alert). You’ll get a notification when the shots are here.',
      );
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!lib) return null;
  const screens = lib.footage.filter((f) => f.kind !== 'presenter');
  const presenter = lib.footage.filter((f) => f.kind === 'presenter');
  return (
    <section>
      <h3>Video footage · {lib.project.name}</h3>
      <p className="muted small">
        Screen recordings Alfred cuts into your AI videos. Record each shot the blueprints ask for
        (5–15 seconds), say what it shows, and upload it. Vertical or horizontal both work.
      </p>
      <input
        className="reject-reason"
        value={description}
        maxLength={300}
        placeholder="What it shows, e.g. Discord alert arriving on a phone"
        aria-label="What the recording shows"
        onChange={(e) => setDescription(e.target.value)}
      />
      <input
        ref={fileInput}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />
      <button
        className="btn btn-wide"
        disabled={busy || !description.trim()}
        onClick={() => fileInput.current?.click()}
      >
        {busy ? 'Uploading…' : 'Upload footage'}
      </button>
      {lib.canRecord && (
        <>
          <p className="muted small">
            Or let Alfred record {lib.project.name} himself: he signs in to its own account, walks
            the dashboard and catches a live alert (also every Monday at 11am).
          </p>
          <button className="btn btn-wide" disabled={busy} onClick={() => void record()}>
            Record {lib.project.name} footage now
          </button>
        </>
      )}
      {message && <p className="small">{message}</p>}
      <p className="muted small">
        {screens.length} recording{screens.length === 1 ? '' : 's'}
        {presenter.length
          ? ` · ${presenter.filter((p) => p.status === 'ready').length} presenter lines ready${presenter.some((p) => p.status === 'pending') ? `, ${presenter.filter((p) => p.status === 'pending').length} being made` : ''}`
          : ''}
      </p>
      {screens.length > 0 && (
        <ul className="routine-list">
          {screens.slice(0, 12).map((f) => (
            <li key={f.id}>
              <span className="small">{f.description}</span>
              <span className="muted small">
                {f.durationSec ? `${Math.round(f.durationSec)}s` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
